import { useMemo } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from '@frontend/i18n';
import { ExtendedAppointment } from '@frontend/schedule-helpers';
import { useScheduleAppointmentInfoPanelShallowStore } from '../../../stores/schedule-appointment-info-panel.store';
import { AppointmentList } from './appointment-list';
import { PanelHeader } from './panel-header';

const getAppointmentsRangeText = (selectedAppointments: ExtendedAppointment[]) => {
  if (!selectedAppointments.length) return '';

  const appointmentTimes = selectedAppointments
    .filter((appointment) => appointment.start && appointment.duration)
    .map((appointment) => {
      const startTime = dayjs(appointment.start);
      const endTime = startTime.add(appointment.duration || 0, 'minute');
      return { start: startTime, end: endTime };
    });

  if (appointmentTimes.length === 0) return '';

  const minStartTime = appointmentTimes.reduce((min, current) =>
    current.start.isBefore(min.start) ? current : min
  ).start;

  const maxEndTime = appointmentTimes.reduce((max, current) => (current.end.isAfter(max.end) ? current : max)).end;

  return `${minStartTime.format('h:mm A')} - ${maxEndTime.format('h:mm A')}`;
};

export const OverlappingAppointmentsPanel = () => {
  const { t } = useTranslation('schedule');
  const { selectedAppointments } = useScheduleAppointmentInfoPanelShallowStore('selectedAppointments');

  const appointmentTimeRange = useMemo(() => getAppointmentsRangeText(selectedAppointments), [selectedAppointments]);

  const appointmentInfoText = t('{{count}} appointments • {{timeRange}}', {
    count: selectedAppointments.length,
    timeRange: appointmentTimeRange,
  });
  const providerName = selectedAppointments[0]?.practitionerName ?? '';

  return (
    <section css={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
      <PanelHeader appointmentsInfoText={appointmentInfoText} providerName={providerName} />
      <AppointmentList appointments={selectedAppointments} />
    </section>
  );
};
